/**
 * ConfirmDialog — Simple confirmation dialog for destructive actions.
 *
 * Used for "End session" and "Delete comment" confirmations within
 * the feedback widget.
 */
import { useRef, useEffect } from "react";
import { FocusTrap } from "focus-trap-react";
import { cn } from "../../utils/cn";

interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly variant?: "danger" | "default";
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      confirmRef.current?.focus();
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmClass =
    variant === "danger" ? "fb-confirm-btn-danger" : "fb-confirm-btn-default";

  return (
    <div
      className="fb-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <FocusTrap focusTrapOptions={{ allowOutsideClick: true }}>
        <div className="fb-dialog">
          <h3 className="fb-dialog-title">{title}</h3>
          <p className="fb-dialog-message">{message}</p>
          <div className="fb-dialog-actions">
            <button
              type="button"
              className="fb-dialog-btn fb-dialog-btn-cancel"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
            <button
              ref={confirmRef}
              type="button"
              className={cn("fb-dialog-btn", confirmClass)}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </FocusTrap>
    </div>
  );
}
